import { gsap } from 'gsap';

class Accordion {
    constructor(group) {
        this.config = {
            rotationInterval: 4000,
            pauseOnHover: true,
            pauseOnFocus: true,
            allowAllClosed: false,
        };

        this.group = group;
        this.elements = this.getElements();

        // Setup rotation state
        this.rotationState = {
            currentIndex: 0,
            intervalId: null,
            isPaused: false,
            progressTimeline: null,
            maxHeight: 0,
            resizeObserver: null,
        };

        this.initialize();
    }

    initialize() {
        this.calculateMaxHeight();
        this.initializeRotation();
        this.setupEventListeners();
        this.initializeFirstItem();
        this.setupResizeObserver();
    }

    getElements() {
        return {
            btns: this.group.querySelectorAll('.js-accordion-btn'),
            content: this.group.querySelectorAll('.js-accordion-content'),
            images: this.group.closest('section').querySelectorAll('.js-accordion-image'),
            progressBars: this.group.querySelectorAll('.js-accordion-progress'),
        };
    }

    setupEventListeners() {
        const { btns, content } = this.elements;

        btns.forEach((btn, index) => {
            // Click handler
            btn.addEventListener('click', (event) => {
                event.preventDefault();
                this.stopRotation();
                gsap.killTweensOf(this.elements.progressBars[index]); // Kill any ongoing animations

                const isOpen = btn.getAttribute('aria-expanded') === 'true';
                const contentPanel = content[index];

                if (!isOpen) {
                    this.open(contentPanel, btn);
                    btn.setAttribute('aria-expanded', 'true');
                    this.rotationState.currentIndex = index;
                } else {
                    const allowAllClosed = this.group.dataset.accordionAllowAllClosed !== 'false';
                    if (allowAllClosed) {
                        this.close(contentPanel, btn);
                        btn.setAttribute('aria-expanded', 'false');
                    }
                }
            });

            // Keyboard navigation
            btn.addEventListener('keydown', (event) => {
                this.handleKeydown(event, btn, index);
            });
        });

        // Pause on hover/focus if enabled
        if (this.config.pauseOnHover) {
            this.group.addEventListener('mouseenter', () => this.pause());
            this.group.addEventListener('mouseleave', () => this.resume());
        }

        if (this.config.pauseOnFocus) {
            btns.forEach((btn) => {
                btn.addEventListener('focus', () => this.pause());
                btn.addEventListener('blur', () => this.resume());
            });
        }
    }

    setupResizeObserver() {
        // Debounce the resize calculation to improve performance
        let resizeTimeout;
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                this.calculateMaxHeight();

                // Re-apply the current state
                const currentBtn = this.elements.btns[this.rotationState.currentIndex];
                const currentContent = this.elements.content[this.rotationState.currentIndex];
                if (currentBtn?.getAttribute('aria-expanded') === 'true') {
                    gsap.set(currentContent, {
                        height: this.rotationState.maxContentHeight,
                    });
                }
            }, 100); // 100ms debounce
        };

        // Create and start observing
        this.rotationState.resizeObserver = new ResizeObserver(handleResize);
        this.rotationState.resizeObserver.observe(this.group);
    }

    calculateMaxHeight() {
        const { content, btns } = this.elements;

        // Calculate total height of all buttons
        const totalButtonsHeight = Array.from(btns).reduce((sum, btn) => (
            sum + btn.offsetHeight
        ), 0);

        // Reset the content max height
        let maxContentHeight = 0;

        // Temporarily make all panels fully visible to measure heights
        content.forEach((panel) => {
            gsap.set(panel, {
                display: 'block',
                height: 'auto',
                hidden: false,
                visibility: 'hidden',
                position: 'relative',
                overflow: 'visible',
            });
        });

        // Find the tallest content panel
        content.forEach((panel) => {
            const innerContent = panel?.querySelector('.js-accordion-inner-content');

            if (innerContent) {
                const contentHeight = innerContent.offsetHeight;
                maxContentHeight = Math.max(maxContentHeight, contentHeight);
            }
        });

        // Store both heights
        this.rotationState.maxHeight = totalButtonsHeight + maxContentHeight;
        this.rotationState.maxContentHeight = maxContentHeight;

        // Reset panels to initial state
        content.forEach((panel, index) => {
            if (index !== this.rotationState.currentIndex) {
                gsap.set(panel, {
                    display: 'none',
                    height: 0,
                    hidden: true,
                    visibility: 'visible',
                });
            } else {
                gsap.set(panel, {
                    visibility: 'visible',
                    height: maxContentHeight,
                });
            }
        });

        // Set container height
        gsap.set(this.group, { height: `${this.rotationState.maxHeight}px` });
    }

    initializeFirstItem() {
        const { btns, content } = this.elements;
        const firstBtn = btns[0];

        if (firstBtn?.getAttribute('aria-expanded') === 'true') {
            const firstContent = content[0];
            gsap.set(firstContent, {
                display: 'block',
                height: 'auto',
            });
            this.updateScreenReaderText(firstBtn, true);
        }
    }

    close(content, btn) {
        if (!content || !btn) return;

        const innerContent = content.querySelector('.js-accordion-inner-content');
        if (!innerContent) return;

        const index = Array.from(this.elements.btns).indexOf(btn);
        this.resetProgress(index);

        if (this.elements.images[index]) {
            gsap.to(this.elements.images[index], {
                opacity: 0,
                duration: 0.3,
                display: 'none',
            });
        }

        gsap.to(content, {
            duration: 0.3,
            height: 0,
            display: 'none',
            onComplete: () => {
                content.setAttribute('hidden', '');
                this.updateScreenReaderText(btn, false);
            },
        });
    }

    open(content, btn) {
        if (!content || !btn) return;

        const { btns, content: contents, images } = this.elements;

        // Close other open accordions
        btns.forEach((otherBtn, index) => {
            if (otherBtn === btn) return;
            if (otherBtn.getAttribute('aria-expanded') === 'true') {
                this.close(contents[index], otherBtn);
                otherBtn.setAttribute('aria-expanded', 'false');
            }
        });

        // Show corresponding image
        const currentIndex = Array.from(btns).indexOf(btn);
        images.forEach((image, index) => {
            if (index === currentIndex) {
                gsap.set(image, { display: 'block' });
                gsap.to(image, {
                    opacity: 1,
                    duration: 0.3,
                });
            }
        });

        content.removeAttribute('hidden');
        this.updateScreenReaderText(btn, true);

        gsap.set(content, {
            display: 'block',
            height: this.rotationState.maxContentHeight,
        });

        const innerContent = content.querySelector('.js-accordion-inner-content');
        if (!innerContent) return;

        gsap.from(content, {
            duration: 0.3,
            height: 0,
        });
    }

    handleKeydown(event, btn, index) {
        const { btns } = this.elements;
        const totalButtons = btns.length;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                btns[(index + 1) % totalButtons]?.focus();
                break;
            case 'ArrowUp':
                event.preventDefault();
                btns[(index - 1 + totalButtons) % totalButtons]?.focus();
                break;
            case 'Home':
                event.preventDefault();
                btns[0]?.focus();
                break;
            case 'End':
                event.preventDefault();
                btns[totalButtons - 1]?.focus();
                break;
            // no default
        }
    }

    // eslint-disable-next-line class-methods-use-this
    updateScreenReaderText(btn, isOpen) {
        const srOnly = btn.querySelector('.sr-only');
        if (srOnly) {
            srOnly.textContent = isOpen ? 'Click to close' : 'Click to open';
        }
    }

    initializeRotation() {
        // Reset any existing progress animation
        this.resetProgress();

        // Start progress animation for current item
        this.animateProgress(this.rotationState.currentIndex);

        this.rotationState.intervalId = setInterval(() => {
            if (!this.rotationState.isPaused) {
                this.rotateToNext();
            }
        }, this.config.rotationInterval);
    }

    rotateToNext() {
        const { btns, content } = this.elements;
        const { currentIndex } = this.rotationState;
        const nextIndex = (currentIndex + 1) % btns.length;

        // Reset current progress
        this.resetProgress(currentIndex);

        // Close current
        const currentBtn = btns[currentIndex];
        const currentContent = content[currentIndex];
        this.close(currentContent, currentBtn);
        currentBtn.setAttribute('aria-expanded', 'false');

        // Open next and start its progress
        const nextBtn = btns[nextIndex];
        const nextContent = content[nextIndex];
        this.open(nextContent, nextBtn);
        nextBtn.setAttribute('aria-expanded', 'true');
        this.animateProgress(nextIndex);

        this.rotationState.currentIndex = nextIndex;
    }

    animateProgress(index) {
        const progressBar = this.elements.progressBars[index];
        if (!progressBar) return;

        // Kill any existing timeline
        if (this.rotationState.progressTimeline) {
            this.rotationState.progressTimeline.kill();
        }

        // Create new timeline
        this.rotationState.progressTimeline = gsap.timeline();
        this.rotationState.progressTimeline.fromTo(
            progressBar,
            { scaleX: 0 },
            {
                scaleX: 1,
                duration: this.config.rotationInterval / 1000,
                ease: 'none',
            },
        );
    }

    resetProgress(specificIndex = null) {
        const { progressBars } = this.elements;

        // Kill existing timeline
        if (this.rotationState.progressTimeline) {
            this.rotationState.progressTimeline.kill();
            this.rotationState.progressTimeline = null;
        }

        progressBars.forEach((bar, index) => {
            if (specificIndex === null || specificIndex === index) {
                gsap.set(bar, { scaleX: 0 });
            }
        });
    }

    pause() {
        this.rotationState.isPaused = true;
        // Pause only the progress timeline
        if (this.rotationState.progressTimeline) {
            this.rotationState.progressTimeline.pause();
        }
    }

    resume() {
        this.rotationState.isPaused = false;
        // Resume only the progress timeline
        if (this.rotationState.progressTimeline) {
            this.rotationState.progressTimeline.resume();
        }
    }

    stopRotation() {
        if (this.rotationState.intervalId) {
            clearInterval(this.rotationState.intervalId);
            this.rotationState.intervalId = null;
            this.resetProgress();
        }
    }

    destroy() {
        // Clean up the resize observer
        if (this.rotationState.resizeObserver) {
            this.rotationState.resizeObserver.disconnect();
            this.rotationState.resizeObserver = null;
        }

        // Clean up the rotation interval
        this.stopRotation();
    }
}

// eslint-disable-next-line import/prefer-default-export
export { Accordion };
